import React, { useCallback } from "react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { updateAnswer } from "../../store/actions/answers";
import { changeNavigation } from "../../store/actions/navigation";
import CheckBoxGroup from "./CheckBoxGroup";

import SelectBox from "./SelectBox";
import Slider from "./Slider";
import TextInput from "./TextInput";
import YesNo from "./YesNo";

const AnswerField = ({ question }) => {
	const dispatch = useDispatch();

	const updateNavigation = useCallback(() => {
		dispatch(
			changeNavigation({
				currentQuestion: question.id,
				surveyStep: question.survey_step,
			}),
		);
	}, [question, dispatch]);

	// this callback will be used to update the store when the answer field changes
	// it dispatchs the question object and an array (size 1) with the selected answer object
	const handleAnswerChange = useCallback(
		(answerText) => {
			let answer = question.answers.find(
				(answerObj) => answerObj.text === answerText,
			);
			if(answer) answer = [answer]
			else answer = []
			dispatch(updateAnswer({ question, answer }));
			updateNavigation();
		},
		[question, dispatch, updateNavigation],
	);

	// this callback is similar to the previous one,
	// it dispatches the question object and an array with the selected answers
	const handleAnswerArrayChange = useCallback(
		(answersTexts) => {
			const answers = answersTexts.map((answerText) =>
				question.answers.find(
					(answerObj) => answerObj.text === answerText,
				),
			);
			dispatch(updateAnswer({ question, answer: answers }));
			updateNavigation();
		},
		[question, dispatch, updateNavigation],
	);

	// handles text answers only, because they don't have an answer object in the question
	// we should store what the user enters
	const handleTextAnswerChange = useCallback(
		(text) => {
			dispatch(
				// if text is empty, record empty answer
				updateAnswer({ question, answer: text ? [{ text }] : [] }),
			);
			updateNavigation();
		},
		[dispatch, question, updateNavigation],
	);

	const answers = useSelector((state) => state.answers);
	const questionAnswer = useMemo(() => {
		return answers[question.id];
	}, [answers, question]);

	const options = useMemo(() => {
		return question.answers
			.slice()
			.sort((answerA, answerB) => {
				if (answerA.list > answerB.list) return 1;
				else if (answerA.list < answerB.list) return -1;
				else return 0;
			})
			.map(({ text }) => text);
	}, [question]);

	if (question.question_type === "text") {
		return (
			<TextInput
				value={questionAnswer.answer[0]?.text}
				type={question.text_type}
				setStateValue={handleTextAnswerChange}
				placeholder={question.placeholder}
				questionNumber={question.id}
			/>
		);
	} else if (question.question_type === "select") {
		return (
			<SelectBox
				value={questionAnswer.answer[0]?.text}
				placeholder={question.placeholder}
				setStateValue={handleAnswerChange}
				questionNumber={question.id}
				options={options}
			/>
		);
	} else if (question.question_type === "slider") {
		return (
			<Slider
				value={questionAnswer.answer[0]?.text}
				setStateValue={handleAnswerChange}
				questionNumber={question.id}
				options={options}
				isAnswered={questionAnswer.touched}
			/>
		);
	} else if (question.question_type === "yes-no") {
		return (
			<YesNo
				value={questionAnswer.answer[0]?.text}
				setStateValue={handleAnswerChange}
				questionNumber={question.id}
			/>
		);
	} else if (question.question_type === "multiple-choice") {
		return (
			<CheckBoxGroup
				value={questionAnswer.answer.map((answer) => answer.text)}
				handleClick={handleAnswerArrayChange}
				noneOption="Just humans"
				questionNumber={question.id}
				options={options}
			/>
		);
	} else return null;
};

export default AnswerField;
